import React, { useCallback, useEffect } from 'react'

// eslint-disable-next-line no-unused-vars
import { IData, ITable } from '../../interfaces/table'

import { TableContext } from '../../contexts/table'
import { PaginateContext } from '../../contexts/paginate'
import { SearchContext } from '../../contexts/search'

import { useTable } from '../../hooks/useTable'
import { usePaginate } from '../../hooks/usePaginate'
import { useSearch } from '../../hooks/useSearch'
import { useOrder } from '../../hooks/useOrder'

import { Filter } from '../Filter'
import { Order } from '../Order'
import { Search } from '../Search'
import { Paginate } from '../Paginate'

import {
  Container,
  Header,
  Controls,
  Table as TableStyled,
  Footer,
  ResponsiveContainer,
  THContainer
} from './styles'
import { OrderContext } from '../../contexts/order'

export const Table: React.FC<ITable> = (props) => {
  const { search, setSearch, isSearching, filter } = useSearch()

  const {
    data,
    columns,
    currentColumns,
    setCurrentColumns,
    currentData,
    setCurrentData
  } = useTable({ ...props, isSearching })

  const {
    order,
    setOrder,
    sort,
    isSelectedColumn,
    onToggle: onToggleOrder,
    isOrdering
  } = useOrder()

  const {
    page,
    setPage,
    index,
    endIndex,
    totalItems,
    setTotalItems,
    isFirstPage,
    isLastPage,
    nextPage,
    previousPage,
    paginate
  } = usePaginate()

  const handlerCurrentData = useCallback(() => {
    let renderData: IData = data

    if (isSearching()) {
      renderData = filter(renderData, search, currentColumns)

      setTotalItems(renderData.length)
    } else {
      setTotalItems(data.length)
    }

    if (isOrdering()) {
      renderData = sort(renderData, order)
    }

    renderData = paginate(renderData, index, endIndex, renderData.length)

    setCurrentData(renderData)
  }, [search, order, currentColumns, index, endIndex, totalItems])

  useEffect(() => {
    handlerCurrentData()
  }, [handlerCurrentData])

  const renderData = useCallback(() => {
    if (currentData.length === 0)
      return (
        <tr>
          <td colSpan={currentColumns.length}>
            <span>Tabela vazia</span>
          </td>
        </tr>
      )

    return currentData.map((item, index) => (
      <tr
        key={index}
        onDoubleClick={(event) => {
          props.onClickAtData && props.onClickAtData(item, event)
        }}
      >
        {columns.map(({ data, renderComponent }, index) => {
          if (!currentColumns.includes(data)) return

          if (renderComponent) {
            return <td key={index}>{renderComponent(item[data])}</td>
          }

          return <td key={index}>{item[data]}</td>
        })}
      </tr>
    ))
  }, [currentData, columns])

  const renderHead = () => {
    return columns.map(({ data, title }) => {
      if (!currentColumns.includes(data)) return null

      return (
        <th
          key={data}
          onClick={() => {
            if (props.order !== false) onToggleOrder(data)
          }}
        >
          <THContainer selected={isSelectedColumn(data)}>
            <span>{title}</span>
            {props.order !== false && <Order column={data} />}
          </THContainer>
        </th>
      )
    })
  }

  return (
    <TableContext.Provider
      value={{
        columns,
        currentColumns,
        setCurrentColumns,
        data,
        currentData,
        setCurrentData
      }}
    >
      <SearchContext.Provider value={{ search, setSearch, isSearching }}>
        <OrderContext.Provider value={{ order, setOrder, sort }}>
          <PaginateContext.Provider
            value={{
              page,
              setPage,
              index,
              endIndex,
              totalItems,
              isFirstPage,
              isLastPage,
              nextPage,
              previousPage
            }}
          >
            <Container className='table-container'>
              <Header className='table-header'>
                <h1>{props.title}</h1>

                <Controls className='table-controls'>
                  {props.search !== false && <Search />}
                  {props.filter !== false && <Filter />}
                </Controls>
              </Header>
              <ResponsiveContainer className='table-responsive'>
                <TableStyled className='table'>
                  <thead>
                    <tr>{renderHead()}</tr>
                  </thead>
                  <tbody>{renderData()}</tbody>
                </TableStyled>
              </ResponsiveContainer>
              <Footer className='table-footer'>
                <Paginate />
              </Footer>
            </Container>
          </PaginateContext.Provider>
        </OrderContext.Provider>
      </SearchContext.Provider>
    </TableContext.Provider>
  )
}
